import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Terminal, Cpu } from "lucide-react";

export function TopRouteLoader({ isNavigating }) {
  return (
    <AnimatePresence>
      {isNavigating && (
        <motion.div
          initial={{ scaleX: 0, opacity: 0 }}
          animate={{ scaleX: 1, opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="fixed top-0 left-0 right-0 h-[3px] z-[9999] origin-left bg-gradient-to-r from-[#4f46e5] via-[#38bdf8] to-[#34d399] shadow-[0_0_12px_#38bdf8]"
        />
      )}
    </AnimatePresence>
  );
}

export default function StylishLoader({ onComplete }) {
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState("INITIALIZING SEARCH INTELLIGENCE...");
  const [isFinished, setIsFinished] = useState(false);

  useEffect(() => {
    // Progress increment timer
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            setIsFinished(true);
            if (onComplete) onComplete();
          }, 300);
          return 100;
        }

        const jump = Math.floor(Math.random() * 14) + 6;
        const next = Math.min(100, prev + jump);

        if (next < 30) {
          setStatusText("INITIALIZING SEARCH INTELLIGENCE...");
        } else if (next < 60) {
          setStatusText("CALIBRATING AEO · GEO · HEO ARCHITECTURES...");
        } else if (next < 90) {
          setStatusText("COMPILING B2B SAAS PIPELINE TELEMETRY...");
        } else {
          setStatusText("SYSTEM OPERATIONAL • LAUNCHING");
        }

        return next;
      });
    }, 70);

    return () => clearInterval(interval);
  }, [onComplete]);

  // Allow clicking anywhere to skip
  const handleSkip = () => {
    setProgress(100);
    setIsFinished(true);
    if (onComplete) onComplete();
  };

  return (
    <AnimatePresence>
      {!isFinished && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ 
            opacity: 0, 
            scale: 1.04, 
            filter: "blur(8px)",
            transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } 
          }}
          onClick={handleSkip}
          className="fixed inset-0 z-[10000] bg-[#070b14] flex flex-col items-center justify-center p-4 cursor-pointer select-none overflow-hidden"
          title="Click to enter immediately"
        >
          {/* Background Ambient Radial Glow */}
          <div 
            className="absolute w-[600px] h-[600px] rounded-full pointer-events-none opacity-40 blur-[130px]"
            style={{
              background: "radial-gradient(circle, rgba(79, 70, 229, 0.35) 0%, rgba(6, 182, 212, 0.25) 50%, transparent 70%)"
            }}
          />

          {/* Cyber Grid Background lines */}
          <div 
            className="absolute inset-0 opacity-[0.12] pointer-events-none"
            style={{
              backgroundImage: "linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)",
              backgroundSize: "48px 48px"
            }}
          />

          {/* Central Animated Monogram Core */}
          <div className="relative mb-8">
            {/* Outer Pulsing Dotted Orbit */}
            <div className="absolute -inset-6 rounded-full border border-dashed border-cyan-400/30 animate-spin [animation-duration:12s] pointer-events-none" />

            {/* Middle Rotating Glowing Conic Ring */}
            <div 
              className="absolute -inset-3 rounded-full opacity-70 blur-[4px] animate-spin [animation-duration:4s]"
              style={{
                background: "conic-gradient(from 0deg, #4f46e5, #06b6d4, #10b981, #4f46e5)"
              }}
            />

            {/* Inner Ring */}
            <div className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-[#090e1a] border border-white/20 shadow-2xl flex items-center justify-center overflow-hidden">
              {/* Subtle Scanline Shimmer */}
              <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-transparent pointer-events-none" />

              {/* Glowing Initials */}
              <span className="font-heading font-black text-2xl sm:text-3xl tracking-tight text-transparent bg-clip-text bg-gradient-to-br from-white via-cyan-200 to-[#38bdf8] drop-shadow-[0_0_15px_rgba(56,189,248,0.5)]">
                KN
              </span>

              {/* Radar Status Dot */}
              <span className="absolute bottom-2 right-2 w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_#34d399] animate-ping" />
              <span className="absolute bottom-2 right-2 w-2 h-2 rounded-full bg-emerald-400" />
            </div>
          </div>

          {/* Name & Title Label */}
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="text-center mb-6"
          >
            <h2 className="font-heading font-black text-lg sm:text-xl text-white tracking-wider uppercase mb-1">
              KANNA NARENDRA KUMAR
            </h2>
            <p className="text-[11px] sm:text-xs font-mono uppercase tracking-widest text-cyan-400/90 font-medium">
              Senior SEO Specialist · AI Search Strategist
            </p>
          </motion.div>

          {/* Progress Bar Container */}
          <div className="w-full max-w-xs sm:max-w-sm mb-4">
            <div className="flex items-center justify-between text-[11px] font-mono mb-1.5 px-0.5">
              <span className="text-white/60 flex items-center gap-1.5">
                <Cpu className="w-3.5 h-3.5 text-cyan-400 animate-pulse" />
                SYSTEM KERNEL
              </span>
              <span className="text-cyan-300 font-bold tabular-nums">
                {progress}%
              </span>
            </div>

            {/* Glowing Laser Progress Track */}
            <div className="w-full h-2 rounded-full bg-white/[0.06] border border-white/10 overflow-hidden p-[1px] relative shadow-inner">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-[#4f46e5] via-[#06b6d4] to-[#34d399] relative"
                style={{ width: `${progress}%` }}
                transition={{ ease: "easeOut", duration: 0.1 }}
              >
                {/* Leading Edge Glow Spark */}
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-white shadow-[0_0_8px_#ffffff]" />
              </motion.div>
            </div>
          </div>

          {/* High-Tech Terminal Status Ticker */}
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/[0.08] text-[10px] sm:text-[11px] font-mono text-white/70 max-w-xs sm:max-w-sm w-full justify-center text-center">
            <Terminal className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 animate-pulse" />
            <span className="truncate">{statusText}</span>
          </div>

          {/* Click to skip hint */}
          <p className="text-[10px] font-mono text-white/30 mt-6 tracking-wider">
            TAP ANYWHERE TO SKIP INTRO
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
